import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { Property, PropertyInputs, LOIInputs } from '../types';
import { DEFAULT_INPUTS } from '../constants';
import { calculateSingleProperty, generateUUID } from '../utils';
import { loadPortfolioFromStorage, savePortfolioToStorage } from '../utils/storage';
import { exportPortfolioToCSV as exportToCSV, importPortfolioFromCSV as importFromCSV } from '../utils/csvUtils';

interface AppContextType {
  // Portfolio
  portfolio: Property[];
  activePropertyId: string | null;
  activeProperty: Property | null;
  selectedProperties: Set<string>;
  showFavoritesOnly: boolean;
  
  // Current inputs
  currentInputs: PropertyInputs & LOIInputs;
  
  // Actions
  setCurrentInputs: (inputs: PropertyInputs & LOIInputs) => void;
  updateCurrentInput: (key: string, value: any) => void;
  saveProperty: () => void;
  loadProperty: (propertyId: string) => void;
  deleteProperty: (propertyId: string) => void;
  togglePropertyFavorite: (propertyId: string) => void;
  togglePropertySelection: (propertyId: string) => void;
  clearSelectedProperties: () => void;
  setShowFavoritesOnly: (show: boolean) => void;
  createNewProperty: () => void;
  toggleFavorite: (propertyId: string) => void;
  exportPortfolioToCSV: () => void;
  importPortfolioFromCSV: (file: File) => void;
  
  // UI State
  activeTab: string;
  setActiveTab: (tab: string) => void;
  projectionDurationYears: number;
  setProjectionDurationYears: (years: number) => void;
  isLoading: boolean;
  loadingMessage: string;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [portfolio, setPortfolio] = useState<Property[]>([]);
  const [activePropertyId, setActivePropertyId] = useState<string | null>(null);
  const [selectedProperties, setSelectedProperties] = useState<Set<string>>(new Set());
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [currentInputs, setCurrentInputs] = useState<PropertyInputs & LOIInputs>(DEFAULT_INPUTS);
  const [activeTab, setActiveTab] = useState('analyzer');
  const [projectionDurationYears, setProjectionDurationYears] = useState(30);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Verarbeitung läuft...');

  // Load portfolio on mount
  useEffect(() => {
    const loaded = loadPortfolioFromStorage();
    setPortfolio(loaded);
    if (loaded.length > 0) {
      setActivePropertyId(loaded[0].id);
      setCurrentInputs(loaded[0].inputs);
    }
  }, []);

  // Save portfolio whenever it changes
  useEffect(() => {
    savePortfolioToStorage(portfolio);
  }, [portfolio]);

  const activeProperty = portfolio.find(p => p.id === activePropertyId) || null;

  const updateCurrentInput = useCallback((key: string, value: any) => {
    setCurrentInputs(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  const saveProperty = useCallback(async () => {
    setIsLoading(true);
    setLoadingMessage('Immobilie wird gespeichert...');
    
    // Small delay to show loading state
    await new Promise(resolve => setTimeout(resolve, 300));
    const calculations = calculateSingleProperty(currentInputs);
    
    if (activePropertyId) {
      // Update existing property
      setPortfolio(prev => prev.map(p => 
        p.id === activePropertyId 
          ? { ...p, name: currentInputs.propertyName, inputs: currentInputs, calculations }
          : p
      ));
    } else {
      // Create new property
      const newProperty: Property = {
        id: generateUUID(),
        name: currentInputs.propertyName,
        isFavorite: false,
        inputs: currentInputs,
        calculations
      };
      setPortfolio(prev => [...prev, newProperty]);
      setActivePropertyId(newProperty.id);
    }
    
    setIsLoading(false);
  }, [activePropertyId, currentInputs]);

  const loadProperty = useCallback((propertyId: string) => {
    const property = portfolio.find(p => p.id === propertyId);
    if (property) {
      setActivePropertyId(property.id);
      setCurrentInputs(property.inputs);
    }
  }, [portfolio]);

  const deleteProperty = useCallback((propertyId: string) => {
    setPortfolio(prev => prev.filter(p => p.id !== propertyId));
    if (activePropertyId === propertyId) {
      const remainingProperties = portfolio.filter(p => p.id !== propertyId);
      if (remainingProperties.length > 0) {
        setActivePropertyId(remainingProperties[0].id);
        setCurrentInputs(remainingProperties[0].inputs);
      } else {
        setActivePropertyId(null);
        setCurrentInputs(DEFAULT_INPUTS);
      }
    }
    setSelectedProperties(prev => {
      const newSet = new Set(prev);
      newSet.delete(propertyId);
      return newSet;
    });
  }, [activePropertyId, portfolio]);

  const togglePropertyFavorite = useCallback((propertyId: string) => {
    setPortfolio(prev => prev.map(p => 
      p.id === propertyId ? { ...p, isFavorite: !p.isFavorite } : p
    ));
  }, []);

  const togglePropertySelection = useCallback((propertyId: string) => {
    setSelectedProperties(prev => {
      const newSet = new Set(prev);
      if (newSet.has(propertyId)) {
        newSet.delete(propertyId);
      } else {
        if (newSet.size < 5) { // Limit to 5 selections
          newSet.add(propertyId);
        }
      }
      return newSet;
    });
  }, []);

  const clearSelectedProperties = useCallback(() => {
    setSelectedProperties(new Set());
  }, []);

  const createNewProperty = useCallback(() => {
    setActivePropertyId(null);
    setCurrentInputs(DEFAULT_INPUTS);
  }, []);

  const toggleFavorite = useCallback((propertyId: string) => {
    togglePropertyFavorite(propertyId);
  }, [togglePropertyFavorite]);

  const exportPortfolioToCSV = useCallback(() => {
    try {
      exportToCSV(portfolio);
    } catch (error) {
      console.error('Export error:', error);
      alert('Fehler beim Exportieren: ' + (error as Error).message);
    }
  }, [portfolio]);

  const importPortfolioFromCSV = useCallback(async (file: File) => {
    setIsLoading(true);
    setLoadingMessage('CSV-Datei wird importiert...');
    
    try {
      const result = await importFromCSV(file);
      
      if (result.warnings.length > 0) {
        console.warn('Import warnings:', result.warnings);
      }
      
      if (result.errors.length > 0) {
        alert('Import-Fehler:\n' + result.errors.join('\n'));
        return;
      }
      
      if (result.success && result.properties.length > 0) {
        setPortfolio(prev => [...prev, ...result.properties]);
        alert(`${result.properties.length} Immobilien erfolgreich importiert${
          result.warnings.length > 0 ? ` (${result.warnings.length} Warnungen)` : ''
        }`);
      } else {
        alert('Keine gültigen Immobilien zum Importieren gefunden');
      }
    } catch (error) {
      console.error('Import error:', error);
      alert('Fehler beim Importieren: ' + (error as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const value: AppContextType = useMemo(() => ({
    portfolio,
    activePropertyId,
    activeProperty,
    selectedProperties,
    showFavoritesOnly,
    currentInputs,
    setCurrentInputs,
    updateCurrentInput,
    saveProperty,
    loadProperty,
    deleteProperty,
    togglePropertyFavorite,
    togglePropertySelection,
    clearSelectedProperties,
    setShowFavoritesOnly,
    createNewProperty,
    toggleFavorite,
    exportPortfolioToCSV,
    importPortfolioFromCSV,
    activeTab,
    setActiveTab,
    projectionDurationYears,
    setProjectionDurationYears,
    isLoading,
    loadingMessage,
  }), [
    portfolio,
    activePropertyId,
    activeProperty,
    selectedProperties,
    showFavoritesOnly,
    currentInputs,
    updateCurrentInput,
    saveProperty,
    loadProperty,
    deleteProperty,
    togglePropertyFavorite,
    togglePropertySelection,
    clearSelectedProperties,
    createNewProperty,
    toggleFavorite,
    exportPortfolioToCSV,
    importPortfolioFromCSV,
    activeTab,
    projectionDurationYears,
    isLoading,
    loadingMessage,
  ]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};